import React, { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import {
  X, Upload as UploadIcon, FileText, CheckCircle2, AlertCircle, Loader2,
  ShoppingBag, Sparkles, ChevronRight, Info,
} from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  order_id: "Order ID", order_date: "Order Date", customer_name: "Customer Name",
  customer_email: "Customer Email", product_name: "Product Name", sku: "SKU",
  quantity: "Quantity", unit_price: "Unit Price", total: "Total", status: "Status",
};

export function ImportWizard({ onClose }) {
  // 3-step flow: 1) Upload  2) Smart Review  3) Importing/Done
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [importRecord, setImportRecord] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [mapping, setMapping] = useState({});
  const [progress, setProgress] = useState(null);
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const onFilePicked = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) { toast.error("Only CSV files are accepted"); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max file size is 50MB"); return; }
    if (f.size === 0) { toast.error("File is empty"); return; }
    setFile(f);
    if (!name) setName(`Import — ${new Date().toLocaleDateString()}`);
  };

  // STEP 1 → upload + analyze
  const uploadAndAnalyze = async () => {
    if (!file) return;
    setCreating(true); setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name || `Import — ${new Date().toLocaleDateString()}`);
      const { data } = await api.post("/imports", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      setImportRecord(data);
      if (data.duplicate_warning) toast("Heads-up: this file looks like a previous upload.");
      setAnalyzing(true);
      const { data: a } = await api.get(`/imports/${data.import_id}/analyze`);
      setAnalysis(a);
      setMapping(a.mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false); setAnalyzing(false);
    }
  };

  // STEP 2 → confirm & run
  const runImport = async () => {
    if (!importRecord || !analysis) return;
    setRunning(true);
    try {
      await api.post(`/imports/${importRecord.import_id}/mapping`, {
        mapping, duplicate_strategy: "skip",
      });
      await api.post(`/imports/${importRecord.import_id}/run`);
      setStep(3);
      const t = setInterval(async () => {
        try {
          const { data } = await api.get(`/imports/${importRecord.import_id}`);
          setProgress(data);
          if (["completed", "failed", "partial"].includes(data.status)) {
            clearInterval(t);
            if (data.status === "completed") toast.success(`Imported ${data.orders_created} orders`);
            else if (data.status === "partial") toast(`Imported ${data.orders_created} orders, ${data.failed_rows} skipped`);
            else toast.error("Import failed");
          }
        } catch (pollErr) {
          // Poll failures are non-fatal (transient 5xx during a fresh import
          // are expected). Log at debug level so future analyzers don't flag
          // this as a silent swallow, then keep polling until success/failure.
          if (process.env.NODE_ENV !== "production") {
            console.debug("[ImportWizard] poll retry:", pollErr?.message || pollErr);
          }
        }
      }, 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setRunning(false); }
  };

  // Re-analyze after generic-CSV suggestion pick
  const resolveField = async (field, header) => {
    const nextMapping = { ...mapping, [field]: header };
    setMapping(nextMapping);
    try {
      await api.post(`/imports/${importRecord.import_id}/mapping`, {
        mapping: nextMapping, duplicate_strategy: "skip",
      });
      const { data: a } = await api.get(`/imports/${importRecord.import_id}/analyze`);
      setAnalysis(a);
      setMapping(a.mapping || nextMapping);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-zinc-900 border border-zinc-800 rounded-none sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]" data-testid="import-wizard">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-orange-500 font-bold inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Smart import · Step {step} of 3
            </p>
            <h2 className="text-lg font-medium text-zinc-50 mt-1" style={{ fontFamily: "var(--font-heading)" }}>
              {["Upload CSV", "Review & confirm", "Importing"][step - 1]}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="wizard-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 pt-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex-1 h-1 rounded-full ${n <= step ? "bg-orange-500" : "bg-zinc-800"}`} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-6">
          {step === 1 && (
            <UploadStep
              file={file} name={name} setName={setName}
              dragOver={dragOver} setDragOver={setDragOver}
              onFilePicked={onFilePicked}
              creating={creating} analyzing={analyzing} uploadPct={uploadPct}
            />
          )}

          {step === 2 && analysis && (
            <ReviewStep analysis={analysis} resolveField={resolveField} />
          )}

          {step === 3 && (
            <ProgressStep progress={progress} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between gap-3">
          {step === 1 && (
            <>
              <span className="text-xs text-zinc-500">Milkath will auto-detect the platform and parse your file.</span>
              <button
                onClick={uploadAndAnalyze} disabled={!file || creating || analyzing}
                className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium text-sm"
                data-testid="wizard-continue-btn"
              >
                {creating ? (<><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>) :
                  analyzing ? (<><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</>) :
                  (<><Sparkles className="h-4 w-4" /> Analyze with AI</>)}
              </button>
            </>
          )}
          {step === 2 && analysis && (
            <>
              <button onClick={() => { setStep(1); setAnalysis(null); }} className="text-sm text-zinc-400 hover:text-white" data-testid="wizard-back-btn">
                Back
              </button>
              <button
                onClick={runImport}
                disabled={running || analysis.needs_user_input || (analysis.valid_rows || 0) === 0}
                className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium text-sm"
                data-testid="wizard-confirm-btn"
              >
                {running ? "Starting…" : (<>Import Data <ChevronRight className="h-4 w-4" /></>)}
              </button>
            </>
          )}
          {step === 3 && progress && ["completed", "failed", "partial"].includes(progress.status) && (
            <button onClick={onClose} className="ml-auto bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="wizard-done-btn">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────── STEP 1: Upload ───────── */
function UploadStep({ file, name, setName, dragOver, setDragOver, onFilePicked, creating, analyzing, uploadPct }) {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFilePicked(e.dataTransfer.files?.[0]); }}
        className={`rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${dragOver ? "border-orange-500 bg-orange-500/5" : "border-zinc-800 bg-zinc-950/40"}`}
        data-testid="wizard-dropzone"
      >
        <UploadIcon className="h-10 w-10 text-zinc-500 mx-auto mb-3" strokeWidth={1.4} />
        <p className="text-zinc-100 text-base">Drop your CSV here</p>
        <p className="mt-1 text-xs text-zinc-500">Milkath auto-detects Shopify, Amazon, Flipkart, WooCommerce &amp; Meesho — no manual mapping needed.</p>
        <p className="text-xs text-zinc-500 mt-2">or</p>
        <label className="inline-flex items-center gap-2 mt-3 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 px-4 py-2 rounded-lg text-sm text-zinc-200 cursor-pointer">
          <FileText className="h-4 w-4" /> Browse files
          <input type="file" accept=".csv" hidden onChange={(e) => onFilePicked(e.target.files?.[0])} data-testid="wizard-file-input" />
        </label>
        {file && (
          <p className="mt-5 text-sm text-zinc-300" data-testid="wizard-file-selected">
            <span className="text-zinc-100">{file.name}</span>
            <span className="text-zinc-500"> · {(file.size / 1024).toFixed(1)} KB</span>
          </p>
        )}
        {(creating || analyzing) && (
          <div className="mt-5">
            <div className="h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500 transition-all" style={{ width: `${creating ? uploadPct : 100}%` }} />
            </div>
            <p className="text-xs text-zinc-500 mt-2">{creating ? `Uploading ${uploadPct}%` : "Detecting platform & parsing file…"}</p>
          </div>
        )}
      </div>
      <div>
        <label className="text-xs font-medium text-zinc-400">Import name (optional)</label>
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Shopify October sales"
          className="mt-1.5 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600"
          data-testid="wizard-name-input"
        />
      </div>
      <div className="text-center text-xs text-zinc-500 flex flex-wrap items-center justify-center gap-3">
        <span>Supports Amazon · Shopify · WooCommerce · Flipkart · Meesho · Generic CSV</span>
        <span className="text-zinc-700">·</span>
        <span>Max 50 MB</span>
      </div>
    </div>
  );
}

/* ───────── STEP 2: Smart Review ───────── */
function ReviewStep({ analysis, resolveField }) {
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); }
    catch { return iso; }
  };
  const dateRange = analysis.summary?.date_from
    ? `${fmtDate(analysis.summary.date_from)} → ${fmtDate(analysis.summary.date_to)}`
    : "—";

  return (
    <div className="space-y-6">
      {/* Template reuse banner */}
      {analysis.template_reused && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2 text-sm text-emerald-300" data-testid="wizard-template-reused">
          <CheckCircle2 className="h-4 w-4" /> We recognized this file format and applied your previous import settings.
        </div>
      )}

      {/* Hero card */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-6" data-testid="review-hero">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <ShoppingBag className="h-5 w-5 text-orange-500" />
          <p className="text-base font-medium text-zinc-50" data-testid="review-platform-value">
            {analysis.detected_platform_label} detected
          </p>
          <span className="text-xs px-1.5 py-0.5 rounded-md bg-zinc-800 text-zinc-300 border border-zinc-700 font-medium capitalize" data-testid="review-report-type">
            {analysis.detected_report_type || "orders"} report
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 font-medium" data-testid="review-confidence">
            {analysis.platform_confidence}% confidence
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          <SummaryTile label="Orders found" value={analysis.summary?.orders ?? "—"} testid="summary-orders" />
          <SummaryTile label="Customers" value={analysis.summary?.customers ?? "—"} testid="summary-customers" />
          <SummaryTile label="Products" value={analysis.summary?.products ?? "—"} testid="summary-products" />
          <SummaryTile label="Date range" value={dateRange} small testid="summary-date-range" />
        </div>
      </div>

      {/* Generic CSV: required field gaps with smart suggestions */}
      {analysis.needs_user_input && analysis.field_suggestions?.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="wizard-needs-input">
          <h3 className="text-sm font-medium text-amber-300 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Help us locate these required fields
          </h3>
          <p className="text-xs text-zinc-400 mt-1">Generic CSV mode — pick the closest column from your file.</p>
          <div className="mt-4 space-y-3">
            {analysis.field_suggestions.map((f) => (
              <SuggestionRow
                key={f.field} field={f.field} suggestions={f.suggestions}
                headers={analysis.headers} onPick={(h) => resolveField(f.field, h)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Warnings (friendly, non-blocking) */}
      {!analysis.needs_user_input && analysis.warnings?.length > 0 && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="wizard-warnings">
          <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-1.5">
            <Info className="h-3 w-3" /> Notes about this file
          </h3>
          <ul className="mt-3 space-y-2">
            {analysis.warnings.map((w, i) => (
              <li key={i} className="text-sm text-zinc-400 flex items-start gap-2" data-testid={`warning-${i}`}>
                <span className="text-zinc-600 mt-0.5">•</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-zinc-500">These are informational. Milkath will import everything that&apos;s available.</p>
        </div>
      )}

      {/* Preview */}
      {!analysis.needs_user_input && analysis.preview_rows?.length > 0 && (
        <PreviewTable mapping={analysis.mapping} previewRows={analysis.preview_rows} />
      )}
    </div>
  );
}

function SummaryTile({ label, value, small, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{label}</p>
      <p className={`mt-2 ${small ? "text-sm" : "text-2xl"} font-semibold tabular-nums text-zinc-50`} style={{ fontFamily: small ? undefined : "var(--font-heading)" }}>
        {value}
      </p>
    </div>
  );
}

function SuggestionRow({ field, suggestions, headers, onPick }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3" data-testid={`wizard-suggest-${field}`}>
      <p className="text-sm text-zinc-100 font-medium">{FIELD_LABELS[field] || field}</p>
      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => onPick(s)}
              className="text-xs px-2.5 py-1 rounded-md bg-orange-500/10 hover:bg-orange-500/20 text-orange-300 border border-orange-500/30 transition-colors"
              data-testid={`wizard-suggest-${field}-pick`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <select
        onChange={(e) => e.target.value && onPick(e.target.value)}
        defaultValue=""
        className="mt-2 bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-200 w-full"
        data-testid={`wizard-suggest-${field}-select`}
      >
        <option value="">Or choose from all columns…</option>
        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
    </div>
  );
}

function PreviewTable({ mapping, previewRows }) {
  // Display a curated set of high-signal columns, regardless of which optional ones the source had.
  const order = ["order_id", "order_date", "customer_name", "customer_email", "product_name", "sku", "quantity", "total", "status"];
  const fields = order.filter((f) => mapping[f]).slice(0, 7);
  return (
    <div data-testid="wizard-preview-table">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Preview · first {previewRows.length} rows</p>
      <div className="overflow-x-auto border border-zinc-800 rounded-lg bg-zinc-950/40">
        <table className="w-full text-xs">
          <thead className="text-zinc-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">#</th>
              {fields.map((f) => (
                <th key={f} className="px-2 py-1.5 text-left font-medium">{FIELD_LABELS[f] || f}</th>
              ))}
              <th className="px-2 py-1.5 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map((r, i) => {
              const tone = r.status === "ok" ? "" : r.status === "error" ? "bg-red-500/5" : "";
              return (
                <tr key={i} className={`border-t border-zinc-800/60 ${tone}`} data-testid={`wizard-preview-row-${r.row}`}>
                  <td className="px-2 py-1.5 text-zinc-500 tabular-nums">{r.row}</td>
                  {fields.map((f) => (
                    <td key={f} className="px-2 py-1.5 text-zinc-300 truncate max-w-[140px]">{String(r.data?.[f] ?? "")}</td>
                  ))}
                  <td className="px-2 py-1.5">
                    {r.status === "ok" ? (
                      <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> OK</span>
                    ) : (
                      <span className="text-red-400 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {r.error?.reason || "Error"}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────── STEP 3: Importing ───────── */
function ProgressStep({ progress }) {
  const [snapshot, setSnapshot] = useState(null);
  const status = progress?.status;
  const importId = progress?.import_id;
  const pct = progress?.progress_pct || 0;
  const stepLabel = (progress?.progress_step || "starting").replace("_", " ");
  const isDone = ["completed", "partial"].includes(status);

  useEffect(() => {
    if (isDone && importId && !snapshot) {
      api.get(`/imports/${importId}/snapshot`).then((r) => setSnapshot(r.data)).catch(() => {});
    }
  }, [isDone, importId, snapshot]);

  const money = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

  // While the import is still processing OR failed, show the compact progress card.
  if (!isDone) {
    return (
      <div className="space-y-6 py-8 max-w-xl mx-auto" data-testid="wizard-progress">
        <div className="text-center">
          {status === "failed" ? (
            <AlertCircle className="h-10 w-10 mx-auto text-red-400" />
          ) : (
            <Loader2 className="h-10 w-10 text-orange-500 mx-auto animate-spin" />
          )}
          <h3 className="mt-4 text-xl font-medium tracking-tight text-zinc-50 capitalize" style={{ fontFamily: "var(--font-heading)" }}>
            {status === "failed" ? "Import failed" : `${stepLabel}…`}
          </h3>
        </div>
        <div>
          <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-zinc-500 mt-2 text-center">{pct}%</p>
        </div>
      </div>
    );
  }

  // ─── Business Snapshot (WOW moment) ───
  return (
    <div className="space-y-6 max-w-2xl mx-auto py-4" data-testid="wizard-snapshot">
      <div className="text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold">Milkath analyzed your business</p>
        <h3 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
          Here's what we found
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Pill label="Revenue"  value={snapshot ? money(snapshot.kpis.revenue) : money(0)} testid="snapshot-revenue" />
        <Pill label="Orders"   value={progress.orders_created || snapshot?.kpis?.orders || 0} testid="snapshot-orders" />
        <Pill label="Products" value={progress.products_created || snapshot?.kpis?.products || 0} testid="snapshot-products" />
        <Pill label="Health"   value={snapshot?.business_health?.score != null ? `${snapshot.business_health.score}/100` : "—"} testid="snapshot-health" />
      </div>

      {snapshot?.top_opportunity && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="snapshot-top-opportunity">
          <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-400 font-bold">Top opportunity</p>
          <p className="mt-1 text-sm text-zinc-100 font-medium">{snapshot.top_opportunity.title}</p>
          {snapshot.top_opportunity.estimated_revenue_impact > 0 && (
            <p className="mt-1 text-xs text-zinc-300">
              <span className="text-zinc-500">Estimated impact:</span> {money(snapshot.top_opportunity.estimated_revenue_impact)}
            </p>
          )}
        </div>
      )}

      {snapshot?.top_risk && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="snapshot-top-risk">
          <p className="text-[10px] uppercase tracking-[0.16em] text-red-400 font-bold">Top risk</p>
          <p className="mt-1 text-sm text-zinc-100 font-medium">{snapshot.top_risk.title}</p>
          <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{snapshot.top_risk.description}</p>
        </div>
      )}

      {snapshot?.next_recommended_action && (
        <p className="text-sm text-zinc-300 text-center" data-testid="snapshot-next-action">
          <span className="text-zinc-500">Next recommended action · </span>
          {snapshot.next_recommended_action}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
        <a href="/dashboard" data-testid="snapshot-goto-dashboard" className="text-center bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
          Go to Dashboard
        </a>
        <a href="/opportunities" data-testid="snapshot-goto-opportunities" className="text-center border border-zinc-700 hover:border-zinc-500 text-zinc-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
          View Opportunities
        </a>
        <a href="/ai-command-center" data-testid="snapshot-goto-ai" className="text-center border border-zinc-700 hover:border-zinc-500 text-zinc-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
          Ask AI
        </a>
      </div>

      {progress?.failed_rows > 0 && (
        <p className="text-xs text-amber-300 text-center">{progress.failed_rows} rows skipped — download the error report from the import row.</p>
      )}
    </div>
  );
}

function Pill({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-3 text-center" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-zinc-100">{value ?? "—"}</p>
    </div>
  );
}
